import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Input, InputLabel, MenuItem, Stack, TextField, Tooltip } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptMatchGame, MatchEnum } from "../../api/DialogPromptApi";
import { IVocabularyWord } from "../../api/VocabularyWordApi";
import AppBarContext from '../../context/AppBarContext';
import { useVocabularyWordListPinyin } from '../../hook/useVocabularyWordListPinyin';
import { arrayBufferToBase64 } from "../../util/base64Utils";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptMatchGameFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string;
    noResponse: string;
}

export const DialogPromptMatchGameForm: React.FC<DialogPromptMatchGameFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordList,
        setVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    } = useVocabularyWordListPinyin({ history, location, setErrorMessage })

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [wordIndexToDelete, setWordIndexToDelete] = React.useState<number | null>(null);

    const [leftMatch, setLeftMatch] = React.useState<MatchEnum | "">("")
    const [rightMatch, setRightMatch] = React.useState<MatchEnum | "">("")

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptMatchGame) => {
        if ('vocabulary_word_list' in dialogPrompt) {
            setVocabularyWordFields(dialogPrompt.vocabulary_word_list);
        } else {
            setVocabularyWordFields([]);
        }
        setLeftMatch(dialogPrompt.left_match as MatchEnum || "")
        setRightMatch(dialogPrompt.right_match as MatchEnum || "")
        setDeleteConfirmationOpen(false)
        setWordIndexToDelete(null)

    }, [setVocabularyWordFields, setLeftMatch, setRightMatch, setDeleteConfirmationOpen, setWordIndexToDelete])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptMatchGame;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearVocabularyWordErrors()

    }, [oldDialogPromptId, clearVocabularyWordErrors, history, location, setErrorMessage, setDialogPromptFields])



    React.useEffect(() => {
        if (leftMatch === "" || rightMatch === "") {
            // TODO handle validation error (shouldn’t happen if form validated)
            return;
        }
        const dialogPrompt = new DialogPromptMatchGame(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse,
            noResponse,
            vocabularyWordList,
            leftMatch,
            rightMatch
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, vocabularyWordList, leftMatch, rightMatch, setNewDialogPrompt])

    const handleAddVocabularyWord = () => {
        const newVocabularyWord: IVocabularyWord = {
            id: 0,
            word: '',
            word_pinyin: [],
            image: '',
            image_url: '',
            audio_sentence: '',
            audio_sentence_pinyin: [],
            audio_sentence_id: 0,
            dialog_prompt_id: 0,
            teacher_english_prompt: '',
            teacher_chinese_prompt: '',
            teacher_chinese_prompt_pinyin: [],
            teacher_chinese_prompt_sentence_id: 0,
            student_response_prompt: '<Answer/>',
            student_response_prompt_pinyin: ['<Answer/>'],
            student_response_prompt_sentence_id: 0,
            word_info_text: null,
            english_text: null
        };
        setVocabularyWordList(prev => [...(Array.isArray(prev) ? prev : []), newVocabularyWord]);
    };

    const handleImageUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // Code to handle the reader result
                let base64String: string;
                const result = reader.result;
                if (typeof result === 'string') {
                    base64String = result;
                } else if (result instanceof ArrayBuffer) {
                    base64String = arrayBufferToBase64(result)
                } else {
                    base64String = ""
                }
                setVocabularyWordValue(index, 'image', base64String)
            };

            reader.readAsDataURL(file);
        }
    };
    const handleDeleteImage = (index: number) => {
        setVocabularyWordValue(index, 'image', null)
        setVocabularyWordValue(index, 'image_url', '')
    };
    const handleDeleteVocabularyWord = (index: number) => {
        setWordIndexToDelete(index);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (wordIndexToDelete !== null) {
            setVocabularyWordList(prevList => prevList.filter((word, index) => index !== wordIndexToDelete));
        }
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const isFirstWordRequired = () => {
        return leftMatch == MatchEnum.FirstWordCharacter || leftMatch == MatchEnum.FirstWordPinyin ||
            rightMatch == MatchEnum.FirstWordCharacter || rightMatch == MatchEnum.FirstWordPinyin
    }

    const isSecondWordRequired = () => {
        return leftMatch == MatchEnum.SecondWordCharacter || leftMatch == MatchEnum.SecondWordPinyin || leftMatch == MatchEnum.SecondWordAudio ||
            rightMatch == MatchEnum.SecondWordCharacter || rightMatch == MatchEnum.SecondWordPinyin || rightMatch == MatchEnum.SecondWordAudio
    }

    const isImageRequired = () => {
        return leftMatch == MatchEnum.Image || rightMatch == MatchEnum.Image
    }

    const isEnglishRequired = () => {
        return leftMatch == MatchEnum.English || rightMatch == MatchEnum.English
    }

    return (<>
        <Stack spacing={2} sx={{ width: '90%', margin: '0 auto', pt: 2 }}>
            <InputLabel id="left-match-label">Left Match</InputLabel>
            <TextField
                select
                value={leftMatch}
                onChange={(e) => setLeftMatch(e.target.value as MatchEnum)}
                fullWidth
                required
            >
                <MenuItem value="" disabled>
                    -- Select Left Match --
                </MenuItem>
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>

            <InputLabel id="right-match-label">Right Match</InputLabel>
            <TextField
                select
                value={rightMatch}
                onChange={(e) => setRightMatch(e.target.value as MatchEnum)}
                fullWidth
                required
            >
                <MenuItem value="" disabled>
                    -- Select Left Match --
                </MenuItem>
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>
        </Stack>
        {vocabularyWordList && vocabularyWordList.map((word, index) => {
            // Assuming word.image contains the ArrayBuffer
            return (

                <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }} key={index}>
                    <h3>Matching Pair {index + 1}</h3>
                    <TextField
                        label="First Chinese Word, Phrase or Sentence"
                        value={word.word}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'word', e.target.value)
                        }
                        required={isFirstWordRequired()}
                    />
                    <TextField
                        label="First Chinese Word Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'word_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'word_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'word_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'word_pinyin')}
                    />
                    <TextField
                        label="Second Chinese Word, Phrase or Sentence (with audio)"
                        value={word.audio_sentence}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'audio_sentence', e.target.value)
                        }
                        required={isSecondWordRequired()}
                    />
                    <TextField
                        label="Second Chinese Word Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'audio_sentence_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'audio_sentence_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'audio_sentence_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'audio_sentence_pinyin')}
                    />
                    <TextField
                        label="English Word, Phrase or Sentence"
                        value={word.english_text}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'english_text', e.target.value)
                        }
                        required={isEnglishRequired()}
                    />
                    <Box>
                        <InputLabel htmlFor="image-upload">Image</InputLabel>
                        <Input
                            type="file"
                            id="image-upload"
                            inputProps={{ accept: "image/*" }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleImageUpload(index, e)}
                        />
                    </Box>

                    <TextField
                        label="Image URL"
                        value={word.image_url || ''}
                        onChange={() => { }} // No-op to keep React happy
                        disabled
                        InputProps={{ readOnly: true }}
                        required={isImageRequired() && !word.image} // Only required if image upload is missing
                        fullWidth
                        error={isImageRequired() && !word.image && !word.image_url}
                        helperText={
                            isImageRequired() && !word.image && !word.image_url
                                ? 'Image is required (upload or use existing URL)'
                                : ''
                        }
                    />
                    <Box sx={{ position: 'relative', display: 'inline-block', margin: '0 auto' }}>
                        {word.image || word.image_url ? (
                            <Stack direction="row">
                                <img
                                    src={word.image ? word.image : word.image_url ? word.image_url : undefined}
                                    alt="Uploaded"
                                    style={{
                                        maxHeight: '64px',
                                        maxWidth: '64px',
                                        height: 'auto',
                                        width: 'auto',
                                        display: 'block',
                                    }}
                                />

                                <Tooltip title="Delete Image">
                                    <Box onClick={() => handleDeleteImage(index)} sx={{ cursor: 'pointer' }}>
                                        <DeleteRoundedIcon sx={{ color: 'red', fontSize: '20px' }} />
                                    </Box>
                                </Tooltip>
                            </Stack>
                        ) : (
                            <Box sx={{ color: isImageRequired() ? 'red' : 'inherit', fontStyle: 'italic', textAlign: 'center' }}>
                                {isImageRequired() ? 'Image required' : 'No image uploaded'}
                            </Box>
                        )}
                    </Box>
                    <Button variant="outlined" onClick={() => handleDeleteVocabularyWord(index)}>
                        Delete Matching Pair
                    </Button>
                </Stack>
            )
        })}
        <Button variant="outlined" onClick={handleAddVocabularyWord}>
            Add Matching Pair
        </Button>
        <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete this vocabulary word {wordIndexToDelete !== null && (<>{vocabularyWordList[wordIndexToDelete].word}</>)} ?
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};
